// supervisor-mission-run.ts
//
// Composes the P5 skeleton (`startSupervisor`: lock -> effective-config -> doctor ->
// reconcile) with P6's bootstrap commit + dispatch loop (architecture §6.1). Separate
// from supervisor.ts so that stays a thin skeleton (Req 11).
import { randomUUID } from "node:crypto";
import { findMissionRunConfigProblem } from "./mission-run-config-preflight.js";
import { missionStatusLabelAfterRun } from "./mission-status-label-after-run.js";
import { MissionStore } from "./mission-store.js";
import { journalReleaseFlowOutcome } from "./release-flow-resume-observability.js";
import { runtimeLimitsFromEffectiveConfig } from "./runtime-limits-from-effective-config.js";
import { startSupervisor } from "./supervisor.js";
import { ensureBootstrapCommit } from "./supervisor-bootstrap.js";
import { readAttemptTimestampsIso } from "./supervisor-dispatch-guards.js";
import { realGitRunner } from "./supervisor-git-runner.js";
import {
  applyOrphanedRecoveriesAfterBootstrap,
  applyQueuedAbortConfirmAtStartup,
  defaultProbeClaudeVersion,
  isMissionAbortedAtStartup,
  reconcileOrphanedTicketsAtStartup,
} from "./supervisor-mission-run-startup.js";
import type { RunMissionInput, RunMissionResult } from "./supervisor-mission-run-types.js";
import { buildSupervisorPolicyEvaluator } from "./supervisor-policy.js";
import { maybeResumeReleaseFlowAfterDone } from "./supervisor-release-flow-resume.js";
import { runSupervisorUntilIdle } from "./supervisor-run-until-idle.js";
import {
  buildStartupBeforeDispatchBlockedResult,
  journalStartupBeforeDispatchBlocked,
} from "./supervisor-startup-before-dispatch-result.js";

export type { RunMissionInput, RunMissionResult } from "./supervisor-mission-run-types.js";

/**
 * Full `run` sequence: skeleton startup, then (if it did not already block)
 * bootstrap commit + dispatch loop until the mission has nothing left to do.
 * Always releases the machine lock before returning, success or failure —
 * after `beforeLockRelease` (CXR-R1: an interrupt's recipe/worker stop). */
export async function runMissionToIdle(input: RunMissionInput): Promise<RunMissionResult> {
  // [B1][N8] ABORTED is permanent — checked lock-free BEFORE startSupervisor: never takes
  // the machine lock, never re-evaluates effective-config/doctor/§18, never dispatches.
  if (await isMissionAbortedAtStartup(input.controlRoot)) {
    return { outcome: "aborted", ticksRun: 0 };
  }
  const start = await startSupervisor(input);
  try {
    // [B1] a queued `abort_confirm` applies even when a branch below blocks.
    if (await applyQueuedAbortConfirmAtStartup(input.controlRoot)) {
      return { outcome: "aborted", ticksRun: 0 };
    }
    if (!start.effectiveConfig.ok) {
      return { outcome: "config_invalid", ticksRun: 0, configError: start.effectiveConfig.message };
    }
    input.onEffectiveConfigLoaded?.(start.effectiveConfig);
    if (start.doctorBlocksRun) {
      return { outcome: "doctor_blocked", ticksRun: 0, doctorFailures: start.doctorFailures };
    }

    // §18 gate, evaluated ONCE at startup by startSupervisor; a blocked `run`
    // exits and journals WHY (per-tick re-check covers mid-run). Mapping:
    // supervisor-startup-before-dispatch-result.ts. A ceiling breach also got
    // its WAITING_HUMAN receipt inside startSupervisor; the `blocked:<code>`
    // label on top keeps `status` naming the reason (e2e cost-limit contract).
    if (start.beforeDispatch?.blocked) {
      await journalStartupBeforeDispatchBlocked(input.controlRoot, start.beforeDispatch.code);
      return buildStartupBeforeDispatchBlockedResult(start.beforeDispatch);
    }

    // H9: mission tickets/profile, once dispatch is possible (mission-run-config-preflight.ts).
    const configError = await findMissionRunConfigProblem(input.missionWorkspace);
    if (configError !== undefined) return { outcome: "config_invalid", ticksRun: 0, configError };
    const config = start.effectiveConfig.config;
    // The mission workspace root IS the execution repo (createMissionWorkspace
    // checks out at worktreePath == missionWorkspace; no nesting in production).
    const executionRepo = input.missionWorkspace;

    // Only built now: the gate passed, so pack playbook I/O (and a
    // missing-playbook contract_error) is worth paying for.
    const buildPrompt = await input.buildPromptFactory();

    const store = await MissionStore.open(input.controlRoot);
    try {
      // Bug C fix (dogfood P10): a PREVIOUS `run` may have crashed/been
      // killed mid-attempt, leaving a ticket "in_progress" AND a stale
      // binding/uncommitted diff on disk. Reconciled + reset BEFORE
      // ensureBootstrapCommit below — see supervisor-mission-run-startup.ts.
      const recoveries = await reconcileOrphanedTicketsAtStartup({
        store,
        controlRoot: input.controlRoot,
        missionWorkspace: input.missionWorkspace,
        executionRepo,
        now: input.now ?? (() => new Date()),
        gitRunner: realGitRunner,
      });

      const evaluatePolicy = buildSupervisorPolicyEvaluator();
      await ensureBootstrapCommit({
        store,
        repoDir: executionRepo,
        gitRunner: realGitRunner,
        evaluatePolicy,
        manifestPaths: input.bootstrapManifestPaths,
        missionId: input.missionId,
        runId: "bootstrap",
      });

      await applyOrphanedRecoveriesAfterBootstrap(
        store,
        input.controlRoot,
        input.missionWorkspace,
        recoveries,
      );

      // P19c: resolve the transport now that `config` exists (credentials/
      // tunables live in effective-config, not the CLI layer). Undefined =>
      // local-only mode, same as before this wiring existed.
      const mattermostTransport =
        input.mattermostTransport ?? (await input.mattermostTransportFactory?.(config));

      const result = await runSupervisorUntilIdle({
        store,
        controlRoot: input.controlRoot,
        missionId: input.missionId,
        missionWorkspace: input.missionWorkspace,
        repoDir: executionRepo,
        retryConfig: config,
        quotaConfig: config,
        runtimeLimitsForKind: (kind) => runtimeLimitsFromEffectiveConfig(config, kind),
        sessionDeps: input.sessionDeps,
        gitRunner: realGitRunner,
        evaluatePolicy,
        buildPrompt,
        workerModel: config.worker_model,
        now: input.now,
        maxTicks: input.maxTicks,
        progress: input.progress,
        beforeSleep: input.beforeSleep,
        machineLockPath: input.machineLockPath,
        mattermostTransport,
        notify: input.notify,
        isInterrupted: input.isInterrupted,
        abortChallengeTtlSec: config.abort_challenge_ttl_sec,
        // §18 gate re-checked per TICK below (startup only covers tick 0).
        // G2 (P16): both values come from the effective-config this run
        // already loaded — never literals, never worker output.
        g2Decision: {
          g2TicketThreshold: config.g2_ticket_threshold,
          g0Delegated: config.g2_waive_delegated_by_g0,
        },
        beforeDispatch: {
          missionWorkspace: input.missionWorkspace,
          config,
          probeClaudeVersion: defaultProbeClaudeVersion,
          attemptTimestampsIso: () => readAttemptTimestampsIso(input.controlRoot),
        },
      });
      // Bug G fix: resumes REVIEWING -> ... -> DONE. Idempotent via
      // gates.json — safe across separate `run` calls (before/after `approve g3`).
      const releaseFlow = await maybeResumeReleaseFlowAfterDone({
        ticketLoopOutcome: result.outcome,
        config,
        store,
        controlRoot: input.controlRoot,
        missionId: input.missionId,
        repoDir: executionRepo,
        resumeInputFactory: input.releaseFlowResumeInputFactory,
      });
      // P24: until now this answer was assigned and read by nobody, so a
      // flow parked on `waiting_review_report` was indistinguishable from a
      // finished one. Journal it here (the console line lives in
      // run-command-cli-wiring.ts) so it survives the terminal.
      if (releaseFlow) await journalReleaseFlowOutcome(store, releaseFlow);
      // Bug I (P15): journal WHY a gate block stopped the run (`blocked:<code>`,
      // same convention as the §18 path). Re-review 2026-09-26 (dogfood): a clean
      // finish journals a label too (H14: one per release state, see
      // missionStatusLabelForReleaseFlow) — else `blocked:gate_not_approved` stuck.
      // X4: a stale gate label is replaced by how this run ended.
      const label = missionStatusLabelAfterRun({
        currentStatus: store.state.status,
        outcome: result.outcome,
        gateBlocked: result.gateBlocked,
        releaseFlow,
      });
      if (label) {
        await store.commit([
          { event_id: randomUUID(), delta: { kind: "mission_status_changed", status: label } },
        ]);
      }
      return { ...result, releaseFlow };
    } finally {
      await store.close();
    }
  } finally {
    try {
      await input.beforeLockRelease?.();
    } finally {
      await start.lock.release();
    }
  }
}
